import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import SafeIcon from '../../common/SafeIcon';
import MoneyInput from '../../common/MoneyInput';
import * as FiIcons from 'react-icons/fi';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

const { FiHome, FiPercent, FiCalendar } = FiIcons;

const HomeCalculator = () => {
  const { user } = useAuth();
  const { saveCalculation, personalInfo } = useData();
  const currency = personalInfo?.investment_currency || 'Currency';

  const [formData, setFormData] = useState({
    homePrice: '',
    downPayment: '20',
    loanTerm: '30',
    interestRate: '6.5',
    propertyTax: '1.2',
    homeInsurance: '0.5',
    pmi: '0.5',
    hoaFees: '0'
  });

  const [results, setResults] = useState(null);

  const calculateMortgage = () => {
    const { homePrice, downPayment, loanTerm, interestRate, propertyTax, homeInsurance, pmi, hoaFees } = formData;
    const price = parseFloat(homePrice);
    const downPaymentAmount = price * (parseFloat(downPayment) / 100);
    const loanAmount = price - downPaymentAmount;
    const monthlyRate = parseFloat(interestRate) / 100 / 12;
    const numPayments = parseInt(loanTerm) * 12;

    const monthlyPI = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    const monthlyPropertyTax = price * (parseFloat(propertyTax) / 100) / 12;
    const monthlyInsurance = price * (parseFloat(homeInsurance) / 100) / 12;
    const monthlyPMI = downPaymentAmount < price * 0.2 ? loanAmount * (parseFloat(pmi) / 100) / 12 : 0;
    const monthlyHOA = parseFloat(hoaFees);
    const totalMonthlyPayment = monthlyPI + monthlyPropertyTax + monthlyInsurance + monthlyPMI + monthlyHOA;

    const totalInterest = (monthlyPI * numPayments) - loanAmount;

    const paymentBreakdown = [
      { name: 'Principal & Interest', value: Math.round(monthlyPI), color: '#F0A030' },
      { name: 'Property Tax', value: Math.round(monthlyPropertyTax), color: '#3B82F6' },
      { name: 'Home Insurance', value: Math.round(monthlyInsurance), color: '#22C55E' },
      { name: 'PMI', value: Math.round(monthlyPMI), color: '#EF4444' },
      { name: 'HOA Fees', value: Math.round(monthlyHOA), color: '#8B5CF6' }
    ].filter(item => item.value > 0);

    const amortizationData = [];
    let remainingBalance = loanAmount;
    for (let month = 1; month <= 12; month++) {
      const interestPayment = remainingBalance * monthlyRate;
      const principalPayment = monthlyPI - interestPayment;
      remainingBalance -= principalPayment;
      amortizationData.push({ month, principal: Math.round(principalPayment), interest: Math.round(interestPayment), balance: Math.round(remainingBalance) });
    }

    const calculationResults = {
      monthlyPayment: Math.round(totalMonthlyPayment),
      monthlyPI: Math.round(monthlyPI),
      downPaymentAmount: Math.round(downPaymentAmount),
      loanAmount: Math.round(loanAmount),
      totalInterest: Math.round(totalInterest),
      totalCost: Math.round(loanAmount + totalInterest),
      paymentBreakdown,
      amortizationData,
      inputs: formData
    };
    setResults(calculationResults);

    if (user) {
      saveCalculation({ type: 'home', inputs: formData, results: calculationResults });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    calculateMortgage();
  };

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <SafeIcon icon={FiHome} className="w-12 h-12 text-primary-500" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Home Purchase Calculator</h1>
          <p className="text-xl text-secondary-300 max-w-3xl mx-auto">
            Calculate your monthly mortgage payments and determine how much home you can afford.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="bg-secondary-900 rounded-xl shadow-lg p-8 border border-primary-500/20">
            <h2 className="text-2xl font-bold text-white mb-6">Loan Details</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <MoneyInput
                label="Home Price"
                name="homePrice"
                value={formData.homePrice}
                onChange={handleChange}
                currency={currency}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Down Payment (%)</label>
                  <div className="relative">
                    <input type="number" step="0.1" name="downPayment" value={formData.downPayment} onChange={handleChange} className="w-full px-4 py-3 pl-10 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none" placeholder="20" />
                    <SafeIcon icon={FiPercent} className="absolute left-3 top-3.5 w-5 h-5 text-primary-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Loan Term (years)</label>
                  <div className="relative">
                    <select name="loanTerm" value={formData.loanTerm} onChange={handleChange} className="w-full px-4 py-3 pl-10 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none appearance-none">
                      <option value="15">15 years</option>
                      <option value="20">20 years</option>
                      <option value="25">25 years</option>
                      <option value="30">30 years</option>
                    </select>
                    <SafeIcon icon={FiCalendar} className="absolute left-3 top-3.5 w-5 h-5 text-primary-500" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Interest Rate (%)</label>
                <div className="relative">
                  <input type="number" step="0.01" name="interestRate" value={formData.interestRate} onChange={handleChange} className="w-full px-4 py-3 pl-10 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none" placeholder="6.5" />
                  <SafeIcon icon={FiPercent} className="absolute left-3 top-3.5 w-5 h-5 text-primary-500" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Property Tax (% ann.)</label>
                  <input type="number" step="0.1" name="propertyTax" value={formData.propertyTax} onChange={handleChange} className="w-full px-4 py-3 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Insurance (% ann.)</label>
                  <input type="number" step="0.1" name="homeInsurance" value={formData.homeInsurance} onChange={handleChange} className="w-full px-4 py-3 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">PMI (% ann.)</label>
                  <input type="number" step="0.1" name="pmi" value={formData.pmi} onChange={handleChange} className="w-full px-4 py-3 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none" />
                </div>
                <MoneyInput
                  label="HOA Fees (monthly)"
                  name="hoaFees"
                  value={formData.hoaFees}
                  onChange={handleChange}
                  currency={currency}
                />
              </div>

              <button type="submit" className="w-full bg-primary-500 hover:bg-primary-400 text-black px-6 py-4 rounded-xl font-bold transition-all shadow-lg shadow-primary-500/20">
                Calculate Mortgage
              </button>
            </form>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
            {results ? (
              <>
                <div className="bg-secondary-900 rounded-xl p-8 border border-primary-500/20">
                  <h3 className="text-xl font-bold text-white mb-6">Payment Breakdown</h3>
                  <div className="text-center mb-6">
                    <p className="text-sm text-secondary-400 uppercase tracking-widest font-black">Monthly Payment</p>
                    <p className="text-4xl font-bold text-primary-500">{currency} {results.monthlyPayment.toLocaleString()}</p>
                  </div>
                  <div className="h-64 mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={results.paymentBreakdown} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                          {results.paymentBreakdown.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#111', border: '1px solid #F0A030', color: '#fff' }}
                          formatter={(value) => [`${currency} ${value.toLocaleString()}`, 'Amount']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2">
                    {results.paymentBreakdown.map((item, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: item.color }}></div>
                          <span className="text-sm text-secondary-300">{item.name}</span>
                        </div>
                        <span className="text-sm font-semibold text-white">{currency} {item.value.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-secondary-900 rounded-xl p-12 text-center border border-primary-500/10 h-full flex flex-col justify-center">
                <SafeIcon icon={FiHome} className="w-16 h-16 text-secondary-700 mx-auto mb-6" />
                <h3 className="text-xl font-bold text-white mb-2">Ready to Calculate?</h3>
                <p className="text-secondary-400">Enter your home details to see your projection.</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default HomeCalculator;